import React, { useRef, useState } from 'react';
import { X, Image as ImageIcon, Upload, Search, Tag as TagIcon, ArrowUp, Plus, Check, Star, MessageCircle, ShieldAlert } from 'lucide-react';
import { Product, ProductOption, Store, Tag, OrderSheet } from '../../types';
import { uploadImage } from '../../api/upload';
import { getAdminTags } from '../../api/tags';
import { getOrderSheets } from '../../api/order-sheets';
import { saveProductTemplate } from '../../api/products';
import {
  formatArrivalDateLabel,
  normalizeArrivalDateValue,
  sortArrivalDateValues,
} from '../../utils/arrivalDates';

type ProductOptionDraft = {
  id?: string;
  name: string;
  price?: number;
  stock: number;
  orderedQuantity?: number;
};

interface ProductEditorProps {
  editingProduct: Partial<Product>;
  setEditingProduct: (product: Partial<Product>) => void;
  onClose: () => void;
  stores: Store[];
  onSave: (productData?: any) => void;
  availableTags?: Tag[];
  orderSheets?: OrderSheet[];
  title?: string;
  submitLabel?: string;
  hideTemplateActions?: boolean;
  hideOrderSheetSelector?: boolean;
}

const sanitizeImageUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

export const ProductEditor: React.FC<ProductEditorProps> = ({
  editingProduct,
  setEditingProduct,
  onClose,
  stores,
  onSave,
  availableTags: initialAvailableTags,
  orderSheets,
  title = '상품 설정',
  submitLabel = '저장하기',
  hideTemplateActions = false,
  hideOrderSheetSelector = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isTagBrowserOpen, setIsTagBrowserOpen] = useState(false);
  const [activeOrderSheets, setActiveOrderSheets] = useState<OrderSheet[]>([]);
  const [arrivalDateInput, setArrivalDateInput] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isTemplateSaved, setIsTemplateSaved] = useState(
    Boolean(editingProduct.templateId),
  );
  const [isOptionMode, setIsOptionMode] = useState(
    !!(editingProduct.options && editingProduct.options.length > 0)
  );
  const [options, setOptions] = useState<ProductOptionDraft[]>(
    editingProduct.options && editingProduct.options.length > 0
      ? [...editingProduct.options]
          .sort(
            (a: ProductOption, b: ProductOption) =>
              (a.position ?? Number.MAX_SAFE_INTEGER) -
              (b.position ?? Number.MAX_SAFE_INTEGER),
          )
          .map((opt: ProductOption) => ({
            id: opt.id,
            name: opt.name,
            price: opt.price,
            stock: opt.stock || 0,
            orderedQuantity: opt.orderedQuantity,
          }))
      : [{ name: '', price: undefined, stock: 0 }]
  );
  const orderedQuantityRef = useRef(
    editingProduct.orderedQuantity ??
      Math.max(
        0,
        Number(editingProduct.totalStock || 0) -
          Number(editingProduct.availableStock ?? editingProduct.totalStock ?? 0),
      ),
  );
  const initialOptionOrderedQuantitiesRef = useRef(
    new Map(
      options
        .filter((option) => option.id)
        .map((option) => [option.id!, Number(option.orderedQuantity || 0)]),
    ),
  );

  const totalIncomingQuantity = isOptionMode
    ? options.reduce((sum, option) => sum + Number(option.stock || 0), 0)
    : Number(editingProduct.totalStock || 0);
  const totalOrderedQuantity = isOptionMode
    ? Array.from(initialOptionOrderedQuantitiesRef.current.values()).reduce(
        (sum, quantity) => sum + quantity,
        0,
      )
    : orderedQuantityRef.current;
  const remainingQuantity = totalIncomingQuantity - totalOrderedQuantity;
  const shouldShowRemainingQuantity = Boolean(
    editingProduct.id &&
      !String(editingProduct.id).startsWith('p-') &&
      (editingProduct.orderedQuantity !== undefined ||
        editingProduct.availableStock !== undefined),
  );

  React.useEffect(() => {
    setIsTemplateSaved(Boolean(editingProduct.templateId));
  }, [editingProduct.id, editingProduct.templateId]);

  React.useEffect(() => {
    if (initialAvailableTags) {
      setAvailableTags(initialAvailableTags);
      return;
    }

    getAdminTags().then(setAvailableTags).catch(console.error);
  }, [initialAvailableTags]);

  React.useEffect(() => {
    const applyOrderSheets = (sheets: OrderSheet[]) => {
      const active = sheets.filter((sheet) => !sheet.isClosed);
      const sorted = [...active].sort((a, b) => {
        const storeA = stores.find((store) => store.id === a.storeId);
        const storeB = stores.find((store) => store.id === b.storeId);

        const posA = storeA?.position ?? 999;
        const posB = storeB?.position ?? 999;

        if (posA !== posB) return posA - posB;

        return (
          new Date(b.deliveryDate).getTime() -
          new Date(a.deliveryDate).getTime()
        );
      });
      setActiveOrderSheets(sorted);
    };

    if (orderSheets) {
      applyOrderSheets(orderSheets);
      return;
    }

    getOrderSheets().then(applyOrderSheets).catch(console.error);
  }, [orderSheets, stores]);

  const arrivalDateFeatureStoreIds = React.useMemo(
    () =>
      new Set(
        (stores || [])
          .filter(
            (store) =>
              store.orderType === 'STORE' &&
              store.supportsArrivalDateFilter === true,
          )
          .map((store) => store.id),
      ),
    [stores],
  );

  const arrivalDateFeatureStores = React.useMemo(
    () =>
      (stores || []).filter((store) => arrivalDateFeatureStoreIds.has(store.id)),
    [arrivalDateFeatureStoreIds, stores],
  );

  const selectedArrivalDateStores = React.useMemo(() => {
    const selectedStoreIds = new Set(
      (activeOrderSheets || [])
        .filter(
          (sheet) =>
            editingProduct.orderSheetIds?.includes(sheet.id) &&
            arrivalDateFeatureStoreIds.has(sheet.storeId),
        )
        .map((sheet) => sheet.storeId),
    );

    return (stores || []).filter((store) => selectedStoreIds.has(store.id));
  }, [
    activeOrderSheets,
    arrivalDateFeatureStoreIds,
    editingProduct.orderSheetIds,
    stores,
  ]);

  const arrivalDateValues = React.useMemo(
    () => sortArrivalDateValues(editingProduct.arrivalDateValues),
    [editingProduct.arrivalDateValues],
  );

  const shouldShowArrivalDateSection =
    arrivalDateFeatureStores.length > 0 || arrivalDateValues.length > 0;
  const canEditArrivalDates =
    selectedArrivalDateStores.length > 0 || arrivalDateValues.length > 0;
  const canSaveTemplate = Boolean(
    editingProduct.id && !String(editingProduct.id).startsWith('p-'),
  );

  const handleAddArrivalDate = () => {
    const normalizedValue = normalizeArrivalDateValue(arrivalDateInput);
    if (!normalizedValue) {
      alert('유효한 입고 날짜를 선택해주세요.');
      return;
    }

    setEditingProduct({
      ...editingProduct,
      arrivalDateValues: sortArrivalDateValues([
        ...(editingProduct.arrivalDateValues || []),
        normalizedValue,
      ]),
    });
    setArrivalDateInput('');
  };

  const handleRemoveArrivalDate = (targetValue: string) => {
    setEditingProduct({
      ...editingProduct,
      arrivalDateValues: (editingProduct.arrivalDateValues || []).filter(
        (value) => value !== targetValue,
      ),
    });
  };

  const handleMoveOptionUp = (targetIndex: number) => {
    if (targetIndex <= 0) {
      return;
    }

    setOptions((current) => {
      const next = [...current];
      [next[targetIndex - 1], next[targetIndex]] = [
        next[targetIndex],
        next[targetIndex - 1],
      ];
      return next;
    });
  };

  const handleSubmit = () => {
    onSave({
      ...editingProduct,
      visibleStoreIds: (editingProduct.visibleStoreIds || []).filter(
        (storeId) => storeId !== '__hidden_all__',
      ),
      isOptionMode,
      options: isOptionMode ? options : [],
    });
  };

  const handleImageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct({ ...editingProduct, image: e.target.value });
  };



  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length === 0) return;

    const currentImages = editingProduct.images || (editingProduct.image ? [editingProduct.image] : []);
    if (currentImages.length + files.length > 20) {
      alert('최대 20장까지만 업로드 가능합니다.');
      return;
    }

    try {
      const uploadPromises = files.map(async (file) => {
        const { url } = await uploadImage(file);
        return url;
      });

      const newUrls = await Promise.all(uploadPromises);
      const updatedImages = [...currentImages, ...newUrls];

      setEditingProduct({
        ...editingProduct,
        images: updatedImages,
        image: updatedImages[0] // Set first image as main image for compatibility
      });
    } catch (error) {
      console.error('Failed to upload image', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      // Reset input so same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    const currentImages = editingProduct.images || (editingProduct.image ? [editingProduct.image] : []);
    const updatedImages = currentImages.filter((_, i) => i !== index);
    setEditingProduct({
      ...editingProduct,
      images: updatedImages,
      image: updatedImages[0] || ''
    });
  };

  const handleSaveTemplate = async () => {
    if (!canSaveTemplate || !editingProduct.id) {
      return;
    }

    try {
      setIsSavingTemplate(true);
      const template = await saveProductTemplate(editingProduct.id);
      setIsTemplateSaved(true);
      setEditingProduct({
        ...editingProduct,
        templateId: template.id,
      });
      alert('상품 템플릿에 저장되었습니다.');
    } catch (error) {
      console.error('Failed to save product template', error);
      alert('상품 템플릿 저장에 실패했습니다.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9000] flex items-end justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-t-[32px] p-6 space-y-5 animate-in slide-in-from-bottom flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-black">{title}</h3>
          <div className="flex items-center gap-2">
            {canSaveTemplate && !hideTemplateActions && (
              <button
                onClick={handleSaveTemplate}
                disabled={isSavingTemplate}
                className={`flex h-8 w-8 items-center justify-center rounded-xl text-xs font-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isTemplateSaved
                    ? 'bg-amber-100 text-amber-500 hover:bg-amber-200'
                    : 'bg-gray-100 text-gray-400 hover:bg-amber-50 hover:text-amber-500'
                }`}
                title="상품 템플릿으로 저장"
              >
                <Star size={16} fill={isTemplateSaved ? 'currentColor' : 'none'} />
              </button>
            )}
            {canSaveTemplate && !hideTemplateActions && (
              <button
                onClick={async () => {
                  if (!window.confirm('이 상품을 최상단으로 끌어올리시겠습니까?')) return;
                  try {
                    const { bumpProduct } = await import('../../api/products');
                    await bumpProduct(editingProduct.id!);
                    alert('상품이 성공적으로 끌어올려졌습니다.');
                    onSave(); // Refresh or close
                  } catch (error) {
                    console.error('Failed to bump product', error);
                    alert('상품 끌어올리기에 실패했습니다.');
                  }
                }}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-black hover:bg-emerald-100 transition-colors"
              >
                <ArrowUp size={14} />
                끌올
              </button>
            )}
            <button onClick={onClose} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"><X size={18} /></button>
          </div>
        </div>

        <div className="space-y-5 overflow-y-auto no-scrollbar pb-10 flex-1 px-1">
          {/* Product Form Inputs */}
          <div className="space-y-4">
            {/* Name & Short Name Row */}
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center justify-between">
                  <span>상품명</span>
                </label>
                <input
                  className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm font-bold focus:border-emerald-500 outline-none transition-all"
                  placeholder="예: 마불갈비찜"
                  value={editingProduct.name || ''}
                  onChange={e => setEditingProduct({ ...editingProduct, name: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">줄임표현</label>
                  <input
                    className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm font-bold outline-none focus:border-emerald-500"
                    placeholder="줄임표현 (영수증용)"
                    value={editingProduct.shortName || ''}
                    onChange={e => setEditingProduct({ ...editingProduct, shortName: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">판매 상태</label>
                  <button
                    onClick={() => setEditingProduct({ ...editingProduct, isClosed: !editingProduct.isClosed })}
                    className={`w-full h-12 rounded-xl text-xs font-black transition-all ${editingProduct.isClosed ? 'bg-red-500 text-white shadow-lg shadow-red-100' : 'bg-white border-2 border-dashed border-gray-200 text-gray-500 hover:border-emerald-500 hover:text-emerald-500'}`}
                  >
                    {editingProduct.isClosed ? '판매 중지됨' : '판매 중'}
                  </button>
                </div>
              </div>
            </div>

            {/* Price & Stock Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">판매가</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full h-12 bg-white border border-gray-200 rounded-2xl px-4 text-sm font-black outline-none focus:border-emerald-500 transition-all shadow-sm"
                    placeholder="0"
                    value={editingProduct.price ? editingProduct.price.toLocaleString() : ''}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setEditingProduct({ ...editingProduct, price: parseInt(val, 10) || 0 });
                    }}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">원</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">입고량 설정</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`w-full h-12 rounded-2xl px-4 text-sm font-black outline-none transition-all shadow-sm ${shouldShowRemainingQuantity ? 'pr-20' : ''} ${isOptionMode ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-emerald-50/30 border-emerald-100/50 text-emerald-700 focus:border-emerald-500'}`}
                    placeholder="0"
                    disabled={isOptionMode}
                    value={isOptionMode ? options.reduce((sum, opt) => sum + (opt.stock || 0), 0) : (editingProduct.totalStock || '')}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setEditingProduct({ ...editingProduct, totalStock: parseInt(val, 10) || 0 });
                    }}
                  />
                  {shouldShowRemainingQuantity && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 bg-white/60 px-2 py-1 rounded-lg backdrop-blur-sm border ${remainingQuantity < 0 ? 'border-red-100' : 'border-emerald-100'}`}>
                      <span className="text-[9px] font-black text-gray-400">
                        {remainingQuantity < 0 ? '부족: ' : '남음: '}
                      </span>
                      <span className={`text-[11px] font-black ${remainingQuantity < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                        {Math.abs(remainingQuantity)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
                <ShieldAlert size={14} /> 주문 취소
              </label>
              <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-3">
                <label className="flex items-center justify-between gap-4 rounded-xl bg-white border border-orange-100 px-4 py-3 cursor-pointer">
                  <div>
                    <p className="text-sm font-bold text-gray-900">고객 주문 취소 허용</p>
                    <p className="mt-1 text-[10px] font-bold text-gray-400">
                      끄면 주문 후 수량 감소와 상품 취소가 불가능합니다.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="h-5 w-5 shrink-0 rounded-md border-gray-300 text-emerald-500 focus:ring-emerald-500 bg-white"
                    checked={editingProduct.isCustomerCancellationEnabled !== false}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        isCustomerCancellationEnabled: e.target.checked,
                      })
                    }
                  />
                </label>
              </div>
            </div>

            {/* Option Management Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between pt-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">옵션 상품 (맛, 사이즈 등)</label>
                <button
                  onClick={() => setIsOptionMode(!isOptionMode)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${isOptionMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                >
                  {isOptionMode ? <Check size={14} /> : <Plus size={14} />}
                  {isOptionMode ? '옵션 모드 해제' : '옵션 모드 켜기'}
                </button>
              </div>

              {isOptionMode && (
                <div className="space-y-3 bg-gray-50/50 p-4 rounded-[24px] border border-gray-100 animate-in slide-in-from-top-1 duration-300">
                  <div className="space-y-2">
                    {options.map((opt, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-xl border border-gray-100/50 shadow-sm relative group/opt space-y-3 sm:space-y-0 sm:flex sm:items-start sm:gap-2"
                      >
                        <div className="w-full sm:flex-1 sm:min-w-0">
                          <input
                            className="w-full h-10 bg-transparent px-1 text-[11px] font-bold outline-none border-b border-gray-100 focus:border-emerald-500"
                            placeholder="옵션명 (예: 매운맛, 대용량 등)"
                            value={opt.name}
                            onChange={e => {
                              const newOptions = [...options];
                              newOptions[idx].name = e.target.value;
                              setOptions(newOptions);
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] gap-2 sm:flex sm:items-center sm:w-auto">
                          <div className="relative min-w-0 sm:w-24">
                            <input
                              type="number"
                              min="0"
                              className="w-full h-10 bg-gray-50/50 rounded-lg px-2 text-[11px] font-black outline-none border border-transparent focus:border-emerald-500 text-right pr-6"
                              placeholder="대표가"
                              value={opt.price ?? ''}
                              onChange={e => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                const newOptions = [...options];
                                newOptions[idx].price = val === '' ? undefined : parseInt(val, 10);
                                setOptions(newOptions);
                              }}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-gray-400">원</span>
                          </div>
                          <div className="relative min-w-0 sm:w-20">
                            <input
                              className="w-full h-10 bg-gray-50/50 rounded-lg px-2 text-[11px] font-black outline-none border border-transparent focus:border-emerald-500 text-right pr-6"
                              placeholder="재고"
                              value={opt.stock}
                              onChange={e => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                const newOptions = [...options];
                                newOptions[idx].stock = parseInt(val, 10) || 0;
                                setOptions(newOptions);
                              }}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-gray-400">개</span>
                          </div>
                          <button
                            onClick={() => handleMoveOptionUp(idx)}
                            disabled={idx === 0}
                            title="옵션 끌어올리기"
                            className="h-10 px-2 shrink-0 flex items-center justify-center gap-1 rounded-lg text-[10px] font-black text-gray-300 hover:text-emerald-600 disabled:opacity-30 disabled:cursor-default transition-colors"
                          >
                            <ArrowUp size={14} />
                            <span>끌올</span>
                          </button>
                          <button
                            onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                            disabled={options.length <= 1}
                            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 disabled:opacity-0 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setOptions([...options, { name: '', price: undefined, stock: 0 }])}
                    className="w-full py-2 bg-white border border-gray-200 rounded-xl text-[10px] font-black text-gray-500 hover:text-emerald-500 hover:border-emerald-500 transition-all active:scale-[0.98]"
                  >
                    + 옵션 추가
                  </button>
                  <p className="text-[9px] text-gray-400 font-bold px-1">
                    * 옵션 가격을 비워두면 <span className="text-emerald-600">위의 판매가</span>가 적용됩니다.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sales Period */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">판매 기간 설정</label>
            <div className="grid grid-cols-2 gap-3 bg-gray-50 p-4 rounded-2xl border border-gray-200">
              <div className="space-y-1">
                <span className="text-[8px] font-black text-gray-400">시작일</span>
                <input
                  type="date"
                  className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs font-bold outline-none"
                  value={editingProduct.salesStartDate?.split('T')[0] || ''}
                  onChange={e => setEditingProduct({ ...editingProduct, salesStartDate: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <span className="text-[8px] font-black text-gray-400">종료일</span>
                <input
                  type="date"
                  className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs font-bold outline-none"
                  value={editingProduct.salesEndDate?.split('T')[0] || ''}
                  onChange={e => setEditingProduct({ ...editingProduct, salesEndDate: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
              <MessageCircle size={14} /> 상품 댓글
            </label>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <label className="flex items-center justify-between gap-4 rounded-2xl bg-white border border-gray-100 px-4 py-3 cursor-pointer">
                <div>
                  <p className="text-sm font-bold text-gray-900">고객 댓글 받기</p>
                  <p className="mt-1 text-[10px] font-bold text-gray-400">
                    활성화하면 고객 상품 상세 화면에 댓글 입력창이 표시됩니다.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded-md border-gray-300 text-emerald-500 focus:ring-emerald-500 bg-white"
                  checked={editingProduct.isCommentEnabled || false}
                  onChange={(e) =>
                    setEditingProduct({
                      ...editingProduct,
                      isCommentEnabled: e.target.checked,
                    })
                  }
                />
              </label>
            </div>
          </div>

          {/* Always-on-Sale & Quick Register */}
          <div className="space-y-4">
            {/*
            임시 주석처리 
             <div className="flex items-center justify-between bg-gray-50 p-4 rounded-2xl border border-gray-200">
              <label htmlFor="isAlwaysOnSale" className="flex flex-col gap-0.5 cursor-pointer">
                <span className="text-sm font-bold text-gray-900">상시 판매 상품</span>
                <span className="text-[10px] font-black text-gray-400">새 주문서가 생성될 때 자동으로 포함됩니다.</span>
              </label>
              <input
                type="checkbox"
                id="isAlwaysOnSale"
                className="w-5 h-5 rounded-md border-gray-300 text-emerald-500 focus:ring-emerald-500 bg-white"
                checked={editingProduct.isAlwaysOnSale || false}
                onChange={e => setEditingProduct({ ...editingProduct, isAlwaysOnSale: e.target.checked })}
              />
            </div> */}
            {/* 
            임시 주석처리 
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                상품 가리기
              </label>
              <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="pr-4">
                  <p className="text-sm font-bold text-gray-900">고객 주문 페이지에서 숨기기</p>
                  <p className="mt-1 text-[10px] font-bold text-gray-400">
                    상품관리에 미리 등록해두고 필요할 때만 공개할 수 있습니다.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded-md border-gray-300 text-red-500 focus:ring-red-500 bg-white"
                  checked={editingProduct.isHidden || false}
                  onChange={(e) =>
                    setEditingProduct({
                      ...editingProduct,
                      isHidden: e.target.checked,
                    })
                  }
                />
              </div>
            </div> */}

            {!hideOrderSheetSelector && (
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">주문서 바로 등록</label>
                <div className="bg-gray-50 p-3 rounded-2xl border border-gray-200 space-y-1 max-h-40 overflow-y-auto no-scrollbar">
                  {activeOrderSheets.length > 0 ? (
                    activeOrderSheets.map(sheet => {
                      const isSelected = editingProduct.orderSheetIds?.includes(sheet.id);
                      const store = stores.find(s => s.id === sheet.storeId);
                      return (
                        <label key={sheet.id} className="flex items-center gap-3 cursor-pointer p-3 hover:bg-white rounded-xl border border-transparent hover:border-gray-200 transition-all shadow-sm hover:shadow-md">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded text-emerald-500 focus:ring-emerald-500 bg-white border-gray-300"
                            checked={isSelected || false}
                            onChange={e => {
                              const ids = editingProduct.orderSheetIds || [];
                              if (e.target.checked) {
                                setEditingProduct({ ...editingProduct, orderSheetIds: [...ids, sheet.id] });
                              } else {
                                setEditingProduct({ ...editingProduct, orderSheetIds: ids.filter(id => id !== sheet.id) });
                              }
                            }}
                          />
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-gray-900 leading-tight">{sheet.label}</span>
                            <span className="text-[10px] font-black text-emerald-500">{store?.name || '알 수 없는 스토어'}</span>
                          </div>
                        </label>
                      );
                    })
                  ) : (
                    <div className="py-8 text-center bg-white/50 rounded-xl border border-dashed border-gray-200">
                      <p className="text-[11px] font-bold text-gray-400">현재 활성화된 주문서가 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {shouldShowArrivalDateSection && (
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  입고날짜
                </label>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                  {selectedArrivalDateStores.length > 0 && (
                    <p className="text-[10px] font-black text-emerald-600">
                      적용 매장: {selectedArrivalDateStores.map((store) => store.name).join(', ')}
                    </p>
                  )}

                  {!canEditArrivalDates ? (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-3">
                      <p className="text-[11px] font-bold text-gray-600">
                        입고날짜 필터를 사용하는 매장 주문서를 먼저 선택해주세요.
                      </p>
                      {arrivalDateFeatureStores.length > 0 && (
                        <p className="mt-1 text-[10px] font-bold text-gray-400">
                          사용 중인 매장: {arrivalDateFeatureStores.map((store) => store.name).join(', ')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={arrivalDateInput}
                          onChange={e => setArrivalDateInput(e.target.value)}
                          className="flex-1 h-11 rounded-2xl border border-gray-200 bg-white px-4 text-xs font-bold outline-none focus:border-emerald-500"
                        />
                        <button
                          type="button"
                          onClick={handleAddArrivalDate}
                          className="px-4 h-11 rounded-2xl bg-gray-900 text-white text-xs font-black"
                        >
                          추가
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {arrivalDateValues.length > 0 ? (
                          arrivalDateValues.map((value) => (
                            <span
                              key={value}
                              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[10px] font-black text-gray-700 border border-gray-200"
                            >
                              {formatArrivalDateLabel(value)}
                              <button
                                type="button"
                                onClick={() => handleRemoveArrivalDate(value)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <X size={12} strokeWidth={3} />
                              </button>
                            </span>
                          ))
                        ) : (
                          <p className="text-[10px] font-bold text-gray-400">
                            선택된 입고날짜가 없습니다.
                          </p>
                        )}
                      </div>
                      <p className="text-[9px] font-bold text-gray-400">
                        구매자 페이지에서는 선택한 날짜별 버튼으로 상품을 골라 볼 수 있습니다.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center justify-between">
              <span className="flex items-center gap-1"><ImageIcon size={14} /> 상품 사진 설정</span>
              <span className="text-emerald-500">{(editingProduct.images?.length || (editingProduct.image ? 1 : 0))}/10</span>
            </label>
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200 space-y-4">
              <div className="flex flex-wrap gap-2">
                {(editingProduct.images || (editingProduct.image ? [editingProduct.image] : [])).map((img, idx) => (
                  <div key={idx} className="relative w-20 h-20 group">
                    <img src={sanitizeImageUrl(img)} className="w-full h-full object-cover rounded-xl border border-gray-200" alt={`Preview ${idx + 1}`} />
                    <button
                      onClick={() => removeImage(idx)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                    >
                      <X size={12} strokeWidth={3} />
                    </button>
                    {idx === 0 && (
                      <div className="absolute bottom-0 left-0 right-0 bg-emerald-500/80 text-white text-[8px] font-black text-center py-0.5 rounded-b-xl">
                        대표 사진
                      </div>
                    )}
                  </div>
                ))}
                {(editingProduct.images?.length || (editingProduct.image ? 1 : 0)) < 10 && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 bg-white flex flex-col items-center justify-center text-gray-400 gap-1 cursor-pointer hover:border-emerald-500 hover:text-emerald-500 transition-all active:scale-95"
                  >
                    <Upload size={18} />
                    <span className="text-[8px] font-black">추가하기</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileUpload} />
                <input
                  className="w-full h-9 bg-white border border-gray-200 rounded-lg px-3 text-[10px] font-bold outline-none focus:border-emerald-500 transition-colors"
                  placeholder="또는 URL로 이미지 추가"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const url = (e.target as HTMLInputElement).value.trim();
                      if (url) {
                        const current = editingProduct.images || (editingProduct.image ? [editingProduct.image] : []);
                        if (current.length < 20) {
                          const updated = [...current, url];
                          setEditingProduct({ ...editingProduct, images: updated, image: updated[0] });
                          (e.target as HTMLInputElement).value = '';
                        } else {
                          alert('최대 20장까지만 가능합니다.');
                        }
                      }
                    }
                  }}
                />
                <p className="text-[9px] text-gray-400 font-medium">* 5MB 이상의 사진은 자동으로 압축됩니다.</p>
              </div>
            </div>
          </div>

          {/* Tags Section */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
              <TagIcon size={14} /> 태그 설정
            </label>
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200">
              <div className="flex flex-wrap gap-2 mb-3">
                {editingProduct.tags?.map((tagName, idx) => (
                  <span
                    key={idx}
                    className="bg-emerald-500 text-white text-[10px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm shadow-emerald-100 animate-in zoom-in duration-200"
                  >
                    #{tagName}
                    <button
                      onClick={() => setEditingProduct({
                        ...editingProduct,
                        tags: editingProduct.tags?.filter(t => t !== tagName) || []
                      })}
                      className="hover:scale-110 active:scale-90 transition-transform"
                    >
                      <X size={12} strokeWidth={3} />
                    </button>
                  </span>
                ))}
                {(!editingProduct.tags || editingProduct.tags.length === 0) && (
                  <p className="text-[10px] font-bold text-gray-400 py-1 px-1">선택된 태그가 없습니다.</p>
                )}
              </div>
              <button
                onClick={() => setIsTagBrowserOpen(true)}
                className="w-full h-11 bg-white border border-gray-200 rounded-xl flex items-center justify-center gap-2 text-xs font-black text-gray-600 hover:border-emerald-500 hover:text-emerald-500 transition-all active:scale-[0.98] shadow-sm hover:shadow-md"
              >
                <Search size={14} />
                태그 선택하기
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">상세 내용</label>
            <textarea className="w-full h-20 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium resize-none focus:outline-none focus:border-emerald-500" placeholder="상품에 대한 설명을 입력하세요" value={editingProduct.description || ''} onChange={e => setEditingProduct({ ...editingProduct, description: e.target.value })} />
          </div>
        </div>

        <div className="flex gap-2 shrink-0 pt-2 border-t">
          <button onClick={onClose} className="flex-1 h-12 bg-gray-100 rounded-xl font-bold text-gray-500">취소</button>
          <button
            onClick={handleSubmit}
            className="flex-[2] h-12 bg-gray-900 text-white rounded-xl font-black shadow-lg"
          >
            {submitLabel}
          </button>
        </div>
      </div>

      {/* Tag Browser Modal */}
      {isTagBrowserOpen && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setIsTagBrowserOpen(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-[32px] p-8 space-y-6 flex flex-col max-h-[70vh] shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-xl font-black text-gray-900">태그 둘러보기</h4>
                <p className="text-xs font-bold text-gray-400 mt-0.5">전체 태그 중에서 선택할 수 있습니다.</p>
              </div>
              <button onClick={() => setIsTagBrowserOpen(false)} className="p-2.5 bg-gray-100 rounded-2xl hover:bg-gray-200 transition-colors"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar py-2">
              <div className="flex flex-wrap gap-2.5">
                {availableTags.map((tag) => {
                  const isSelected = editingProduct.tags?.includes(tag.name);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => {
                        if (isSelected) {
                          setEditingProduct({ ...editingProduct, tags: editingProduct.tags?.filter(t => t !== tag.name) || [] });
                        } else {
                          setEditingProduct({ ...editingProduct, tags: [...(editingProduct.tags || []), tag.name] });
                        }
                      }}
                      className={`px-4 py-2.5 rounded-[18px] text-xs font-black transition-all border-2 ${isSelected
                        ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-100 scale-95'
                        : 'bg-gray-50 border-transparent text-gray-500 hover:bg-white hover:border-gray-200 active:scale-95'}`}
                    >
                      # {tag.name}
                    </button>
                  );
                })}
                {availableTags.length === 0 && (
                  <div className="w-full text-center py-12 space-y-3">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-300">
                      <TagIcon size={24} />
                    </div>
                    <p className="text-xs font-bold text-gray-400">등록된 태그가 없습니다.</p>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsTagBrowserOpen(false)}
              className="w-full h-14 bg-gray-900 text-white rounded-2xl font-black text-sm shadow-xl hover:bg-black transition-all active:scale-[0.98]"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
